<template>
  <section>
    <div
      v-if="selectedPeople.length > 0 || activeFilter === 'candidate' || reviewMode"
      class="people-filterbar"
    >
      <b-form-input
        v-if="activeFilter === 'candidate'"
        class="people-candidate-filter"
        type="search"
        :value="candidateFilter"
        :debounce="250"
        :placeholder="$t('people_filter_unconfirmed')"
        @input="$emit('filter-candidates', $event)"
      />
      <b-select
        v-if="reviewMode"
        class="people-face-sort"
        :value="faceSort"
        @input="$emit('sort-faces', $event)"
      >
        <option value="newest">{{ $t('people_newest_first') }}</option>
        <option value="oldest">{{ $t('people_oldest_first') }}</option>
      </b-select>
      <b-button-group
        v-if="selectedPeople.length > 0 && !reviewMode && activeFilter !== 'hidden'"
        class="people-selection-actions"
      >
        <b-dropdown
          v-if="activeFilter === 'candidate'"
          class="people-merge-into-confirmed"
          variant="primary"
          size="sm"
          :text="$t('people_merge_into_confirmed')"
          :disabled="confirmedTargets.length === 0"
        >
          <b-dropdown-item
            v-for="person in confirmedTargets"
            :key="person.id"
            @click="$emit('merge-into-confirmed', person.id)"
          >
            {{ person.name }}
            <small>{{ person.face_count }} {{ $t('people_photos') }}</small>
          </b-dropdown-item>
        </b-dropdown>
        <b-button
          v-if="selectedPeople.length > 1"
          class="people-merge-selected"
          variant="danger"
          size="sm"
          @click="$emit('merge-selected')"
        >
          {{ $t('people_merge_selected') }}
        </b-button>
        <b-button
          class="people-hide-selected"
          variant="outline-light"
          size="sm"
          @click="$emit('hide-selected')"
        >
          {{ $t('people_hide_selected') }}
        </b-button>
      </b-button-group>
      <div v-if="activeFilter === 'candidate'" class="people-rematch-actions">
        <b-button
          class="people-rematch"
          variant="outline-primary"
          size="sm"
          :disabled="rematchActive || total === 0"
          @click="$emit('rematch')"
        >
          <b-spinner v-if="rematchActive" small />
          {{ rematchActive
            ? $t('people_rematch_progress', { processed: rematch.processed, total: rematch.total })
            : $t('people_rematch') }}
        </b-button>
        <b-button
          v-if="rematchActive"
          class="people-rematch-cancel"
          variant="outline-light"
          size="sm"
          @click="$emit('cancel-rematch')"
        >
          {{ $t('people_rematch_cancel') }}
        </b-button>
        <small v-else-if="rematch && rematch.status === 'completed'" class="people-rematch-result">
          {{ $tc('people_rematch_result', rematch.matched, { count: rematch.matched }) }}
          <span v-if="rematch.failed">{{ $t('people_rematch_failed', { count: rematch.failed }) }}</span>
        </small>
      </div>
      <div v-if="activeFilter === 'ungrouped'" class="people-rematch-actions">
        <b-button
          class="people-face-rematch"
          variant="outline-primary"
          size="sm"
          :disabled="faceRematchActive || total === 0"
          @click="$emit('rematch-faces')"
        >
          <b-spinner v-if="faceRematchActive" small />
          {{ faceRematchActive
            ? $t('people_rematch_progress', { processed: faceRematch.processed, total: faceRematch.total })
            : $t('people_face_rematch') }}
        </b-button>
        <b-button
          v-if="faceRematchActive"
          class="people-face-rematch-cancel"
          variant="outline-light"
          size="sm"
          @click="$emit('cancel-face-rematch')"
        >
          {{ $t('people_rematch_cancel') }}
        </b-button>
      </div>
    </div>

    <div
      v-if="selectedPeople.length > 0"
      class="people-selection-indicator"
      role="status"
      aria-live="polite"
    >
      {{ selectedPeople.length }} {{ $t('people_selected') }}
    </div>

    <div v-if="items.length === 0" class="people-empty">{{ $t('people_empty') }}</div>
    <div v-else class="people-grid">
      <PersonCard
        v-for="item in items"
        :key="`${activeFilter}-${item.id}`"
        :item="item"
        :review="activeFilter === 'review' || activeFilter === 'ungrouped'"
        :ungrouped="activeFilter === 'ungrouped'"
        :selected="selectedPeople.includes(item.id)"
        :confirmed-targets="confirmedTargets"
        :draft-targets="draftTargets"
        :review-target="reviewTargets[item.id]"
        @toggle="$emit('toggle-person', item.id)"
        @open="openPerson(item)"
        @context="openContextMenu(item, $event)"
        @show-source="$emit('show-source', item)"
        @set-review-target="$emit('set-review-target', item.id, $event)"
        @approve="$emit('approve-review', item)"
        @ignore="$emit('ignore-review', item)"
      />
    </div>

    <div
      v-if="contextMenu.visible"
      ref="contextMenu"
      class="people-context-menu"
      :style="contextMenuStyle"
      role="menu"
      @pointerdown.stop
      @contextmenu.prevent
    >
      <template v-if="reviewMode">
        <PersonAssignmentSelect
          class="people-context-assignment"
          :value="contextTarget"
          :confirmed-targets="confirmedTargets"
          :draft-targets="draftTargets"
          @input="assignContextSelection"
        />
        <button
          class="people-context-unconfirmed"
          type="button"
          role="menuitem"
          @click="contextAction('move-selected-to-unconfirmed')"
        >
          <i class="bi bi-person-plus" /> {{ $t('people_move_to_unconfirmed') }}
        </button>
        <button
          class="people-context-ignore"
          type="button"
          role="menuitem"
          @click="contextAction('ignore-selected-reviews')"
        >
          <i class="bi bi-eye-slash" /> {{ $t('people_ignore_selected') }}
        </button>
      </template>
      <template v-else>
        <button
          v-if="contextMenu.selectionCount > 1"
          class="people-context-merge"
          type="button"
          role="menuitem"
          @click="contextAction('merge-selected')"
        >
          <i class="bi bi-intersect" /> {{ $t('people_merge_selected') }}
        </button>
        <button
          class="people-context-hide"
          type="button"
          role="menuitem"
          @click="contextAction('hide-selected')"
        >
          <i class="bi bi-eye-slash" /> {{ $t('people_hide_selected') }}
        </button>
      </template>
    </div>

    <div v-if="total > items.length" ref="loadMoreTrigger" class="people-load-more">
      <b-button variant="outline-primary" :disabled="loadingMore || curationPending" @click="requestMore">
        <b-spinner v-if="loadingMore" small />
        {{ $t('people_load_more') }}
      </b-button>
    </div>
  </section>
</template>

<script>
import PersonAssignmentSelect from './PersonAssignmentSelect.vue'
import PersonCard from './PersonCard.vue'

export default {
  components: { PersonAssignmentSelect, PersonCard },
  props: {
    activeFilter: { type: String, required: true },
    candidateFilter: { type: String, default: '' },
    faceSort: { type: String, default: 'newest' },
    curationPending: { type: Boolean, default: false },
    items: { type: Array, required: true },
    total: { type: Number, required: true },
    selectedPeople: { type: Array, required: true },
    allTargets: { type: Array, required: true },
    confirmedTargets: { type: Array, default: () => [] },
    draftTargets: { type: Array, default: () => [] },
    reviewTargets: { type: Object, required: true },
    rematch: { type: Object, default: null },
    faceRematch: { type: Object, default: null },
  },
  computed: {
    rematchActive() {
      return ['pending', 'running'].includes(this.rematch?.status)
    },
    faceRematchActive() {
      return ['pending', 'running'].includes(this.faceRematch?.status)
    },
    reviewMode() {
      return this.activeFilter === 'review' || this.activeFilter === 'ungrouped'
    },
    itemCount() {
      return this.items.length
    },
    contextMenuStyle() {
      return {
        left: `${this.contextMenu.x}px`,
        top: `${this.contextMenu.y}px`,
      }
    },
  },
  data() {
    return {
      contextMenu: {
        visible: false,
        x: 0,
        y: 0,
        selectionCount: 0,
      },
      loadingMore: false,
      contextTarget: null,
      loadMoreObserver: null,
    }
  },
  watch: {
    activeFilter() {
      this.closeContextMenu()
      this.loadingMore = false
      this.$nextTick(this.observeLoadMore)
    },
    itemCount() {
      this.loadingMore = false
      this.$nextTick(this.observeLoadMore)
    },
    total() {
      this.$nextTick(this.observeLoadMore)
    },
    curationPending(pending) {
      if (pending) {
        this.loadingMore = false
        this.loadMoreObserver?.disconnect()
      } else {
        this.$nextTick(this.observeLoadMore)
      }
    },
  },
  mounted() {
    document.addEventListener('pointerdown', this.closeContextMenu)
    document.addEventListener('keydown', this.handleKeydown)
    window.addEventListener('scroll', this.closeContextMenu, true)
    window.addEventListener('resize', this.closeContextMenu)
    if (typeof IntersectionObserver !== 'undefined') {
      this.loadMoreObserver = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) this.requestMore()
      }, { rootMargin: '300px' })
      this.observeLoadMore()
    }
  },
  beforeDestroy() {
    document.removeEventListener('pointerdown', this.closeContextMenu)
    document.removeEventListener('keydown', this.handleKeydown)
    window.removeEventListener('scroll', this.closeContextMenu, true)
    window.removeEventListener('resize', this.closeContextMenu)
    this.loadMoreObserver?.disconnect()
  },
  methods: {
    openPerson(item) {
      this.closeContextMenu()
      this.$emit('open-person', item)
    },
    openContextMenu(item, event) {
      if (this.activeFilter === 'hidden') return
      const alreadySelected = this.selectedPeople.includes(item.id)
      if (!alreadySelected) this.$emit('select-only-person', item.id)
      this.contextMenu = {
        visible: true,
        x: event.clientX,
        y: event.clientY,
        selectionCount: alreadySelected ? this.selectedPeople.length : 1,
      }
      this.$nextTick(this.boundContextMenu)
    },
    boundContextMenu() {
      const menu = this.$refs.contextMenu
      if (!menu) return
      const margin = 8
      this.contextMenu.x = Math.max(margin, Math.min(
        this.contextMenu.x,
        window.innerWidth - menu.offsetWidth - margin,
      ))
      this.contextMenu.y = Math.max(margin, Math.min(
        this.contextMenu.y,
        window.innerHeight - menu.offsetHeight - margin,
      ))
    },
    closeContextMenu() {
      this.contextMenu.visible = false
      this.contextTarget = null
    },
    contextAction(action) {
      this.closeContextMenu()
      this.$emit(action)
    },
    assignContextSelection(target) {
      if (target == null) return
      this.closeContextMenu()
      this.$emit('assign-selected-reviews', target)
    },
    handleKeydown(event) {
      if (event.key === 'Escape') this.closeContextMenu()
    },
    observeLoadMore() {
      if (!this.loadMoreObserver) return
      this.loadMoreObserver.disconnect()
      if (this.$refs.loadMoreTrigger) this.loadMoreObserver.observe(this.$refs.loadMoreTrigger)
    },
    requestMore() {
      if (this.loadingMore || this.curationPending || this.items.length >= this.total) return
      this.loadingMore = true
      this.$emit('load-more')
    },
  },
}
</script>

<style scoped>
.people-filterbar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin: 1.5rem 0 1rem;
}

.people-candidate-filter {
  width: min(20rem, 40vw);
  margin-right: auto;
}

.people-face-sort {
  width: auto;
  min-width: 11rem;
  margin-right: auto;
}

.people-selection-indicator {
  position: fixed;
  z-index: 1050;
  bottom: calc(var(--now-playing-height) + 1rem);
  left: 50%;
  padding: .55rem 1rem;
  border: 1px solid rgba(255, 255, 255, .22);
  border-radius: 999px;
  background: var(--highlight-color);
  box-shadow: 0 6px 20px rgba(0, 0, 0, .3);
  color: white;
  font-weight: 700;
  pointer-events: none;
  transform: translateX(-50%);
}

.people-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 1rem;
}

.people-merge-into-confirmed small {
  margin-left: .5rem;
  color: var(--faded-text-color);
}

.people-rematch-actions {
  display: flex;
  gap: .5rem;
  align-items: center;
  margin-left: auto;
}

.people-rematch-result {
  color: var(--faded-text-color);
}

.people-empty {
  padding: 5rem 1rem;
  color: var(--faded-text-color);
  text-align: center;
}

.people-load-more {
  margin: 1.5rem;
  text-align: center;
}

.people-context-menu {
  position: fixed;
  z-index: 1100;
  display: grid;
  width: min(24rem, calc(100vw - 1rem));
  min-width: min(18rem, calc(100vw - 1rem));
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, .18);
  border-radius: 8px;
  background: #252525;
  box-shadow: 0 8px 24px rgba(0, 0, 0, .45);
}

.people-context-menu button {
  display: flex;
  gap: .6rem;
  align-items: center;
  padding: .65rem .85rem;
  border: 0;
  background: transparent;
  color: var(--text-color);
  text-align: left;
}

.people-context-assignment {
  width: calc(100% - 1.3rem);
  min-width: 0;
  margin: .65rem;
}

.people-context-menu button:hover,
.people-context-menu button:focus {
  background: var(--highlight-color);
  color: white;
  outline: none;
}
</style>
